import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'

import IconBack from '@jarvis.oddle/core/components/Icons/IconBack'
import Div from '@jarvis.oddle/core/components/Div/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H1 } from '@jarvis.oddle/core/components/Typo'
import Button from '@jarvis.oddle/core/components/Button'
import Modal from '@jarvis.oddle/core/components/Modal'

import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  buttonStyles,
  colors,
  marginSizes,
  mobileSideNav,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { headerHeight } from '@jarvis.oddle/core/components/Modal/Elements'
import useCustomisation from 'src/hooks/useCustomisation'
import {
  LanguageModalForm,
  LanguageSelectionRadio
} from 'src/components/molecules/Modals/LanguageSelectorModal'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { makeControlledModal } from 'src/decorators/ModalController'

export const CustomModalHeader = styled(Flex)`
  height: ${headerHeight};
  padding: ${padding[paddingSizes.medium]} ${padding[paddingSizes.large]} 0
    ${padding[paddingSizes.large]};
`

const Container = styled(Div)`
  position: absolute;
  top: 0;
  bottom: 0;
  height: 100%;
  width: ${mobileSideNav.width};
  background-color: ${props => props.backgroundColor || colors.container};
`

// Reference: https://chakra-ui.com/docs/form/button#button-variants
export const GhostButton = styled(Button).attrs(() => ({
  buttonStyle: buttonStyles.outline
}))`
  margin: 0;
  padding: 0;
  &&& {
    border: none;
    box-shadow: none;
  }
`

const StyledBodyHeading = styled(H1).attrs(() => ({
  marginLeft: marginSizes.medium,
  marginBottom: marginSizes.extraLarge,
  normalised: true,
  bold: true
}))`
  margin-top: ${props => props.showLogo && '60px'};
`

// WORKAROUND: this is quite hacky to put component as props
const MobileLanguageDrawer = ({
  modal,
  goToPreviousModal,
  showLogo
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  goToPreviousModal: () => void
  showLogo: boolean
}): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const router = useRouter()
  const { getSectionFromId } = useCustomisation()
  const locales = useAppSelector(selectDetailMenuSite)?.locales

  const [headerBackgroundColor, navigationLinkFontColor] = useMemo(() => {
    const { settings = {} } = getSectionFromId('header')
    return [settings.headerBackgroundColor, settings.navigationLinkFontColor]
  }, [getSectionFromId])

  return (
    <Modal {...modal} withoutFocusTrap slideInAnimation='left'>
      <Container backgroundColor={headerBackgroundColor}>
        <LanguageModalForm
          modal={modal}
          width={mobileSideNav.width}
          backgroundColor={headerBackgroundColor}
          buttonColor={navigationLinkFontColor}
          Header={
            <CustomModalHeader>
              <GhostButton onClick={goToPreviousModal}>
                <IconBack
                  width='27'
                  height='27'
                  fill={navigationLinkFontColor}
                />
              </GhostButton>
            </CustomModalHeader>
          }
          Body={
            <>
              <StyledBodyHeading
                showLogo={showLogo}
                color={navigationLinkFontColor}
              >
                {t('Change language to')}
              </StyledBodyHeading>
              <LanguageSelectionRadio
                locale={router.query.locale as string}
                locales={locales}
                color={navigationLinkFontColor}
                backgroundColor={headerBackgroundColor}
              />
            </>
          }
          buttonText={t('Apply')}
        />
      </Container>
    </Modal>
  )
}

export default makeControlledModal(MobileLanguageDrawer, 'MobileLanguageDrawer')
